import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import logo from '../static/Crunch Time Jobs.png';
import background from '../static/login_background.jpg';
import '../static/user.css';
import { url } from "../Components/url";
import { fetchWithAuth } from "../Components/FetchWithAuth";
import { Spinner } from "react-bootstrap";
import { getDefaultRouteForRole } from "../utils/postLoginRoute";

function Login() {
    const API_BASE_URL = url;
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [usernameInput, setUsernameInput] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [userId, setUserId] = useState(null);
    const awaitingUserDetailsRef = useRef(false);

    const { setUsername } = useContext(UserContext);
    const { setRole } = useContext(RoleContext);

    useEffect(() => {
        document.body.classList.add("login-page");
        return () => {
            document.body.classList.remove("login-page");
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        awaitingUserDetailsRef.current = false;

        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    username: usernameInput,
                    password,
                }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                setErrorMessage(data.message || 'Invalid credentials');
                return;
            }

            if (data.requires_mfa && data.mfa_token) {
                navigate('/mfa-verify', {
                    replace: true,
                    state: {
                        mfa_token: data.mfa_token,
                        userId: data.id,
                        methods: data.methods || ['authenticator', 'email', 'backup'],
                    },
                });
                return;
            }

            setUsername(data.id);

            const roleFromLogin = data.role != null && String(data.role).trim() !== ''
                ? data.role
                : null;
            if (roleFromLogin) {
                setRole(roleFromLogin);
                navigate(getDefaultRouteForRole(roleFromLogin), { replace: true });
                return;
            }

            awaitingUserDetailsRef.current = true;
            setUserId(data.id);
        } catch (error) {
            console.error("Login error:", error);
            setErrorMessage('An error occurred. Please try again.');
        } finally {
            if (!awaitingUserDetailsRef.current) {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE_URL}/get_user/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                if (!res.ok) throw new Error("Unauthorized or failed request");
                const userData = await res.json();
                setRole(userData.role);
                navigate(getDefaultRouteForRole(userData.role), { replace: true });
            } catch (error) {
                console.error("fetchWithAuth user details failed:", error);
                setErrorMessage("Unable to fetch user details.");
                setUserId(null);
            } finally {
                setIsLoading(false);
                awaitingUserDetailsRef.current = false;
            }
        };

        if (userId) {
            fetchDetails();
        }
    }, [userId, API_BASE_URL, navigate, setRole]);

    return (
        <div className="login-container">
            <div className="login-background">
                <img src={background} alt="Background" />
            </div>

            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <img src={logo} alt="Logo" className="login-logo" />
                        <h1 className="login-title">Welcome Back</h1>
                        <p className="login-subtitle">Sign in to your account to continue</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-body">
                        <div className="login-form-group">
                            <label htmlFor="username" className="login-form-label">Username</label>
                            <input
                                type="text"
                                id="username"
                                className="login-form-control"
                                placeholder="Enter your username"
                                value={usernameInput}
                                onChange={(e) => setUsernameInput(e.target.value)}
                                required
                            />
                        </div>

                        <div className="login-form-group">
                            <label htmlFor="password" className="login-form-label">Password</label>
                            <div className="login-password-field">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    className="login-form-control"
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    className="login-password-toggle"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20C7 20 2.73 16.89 1 12c.83-2.34 2.29-4.33 4.18-5.77" />
                                            <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c5 0 9.27 3.11 11 8a11.06 11.06 0 0 1-1.67 3.18" />
                                            <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
                                            <path d="M1 1l22 22" />
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="d-flex justify-content-end mt-1">
                                <Link to="/forgot-password" className="login-link" style={{ fontSize: '0.875rem' }}>Forgot password?</Link>
                            </div>
                        </div>

                        {errorMessage && (
                            <div className="login-error d-flex align-items-center mt-2 text-danger" style={{ fontSize: '0.875rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="me-2" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errorMessage}
                            </div>
                        )}

                        <button type="submit" className="btn btn-primary mt-3" style={{ width: '100%', padding: '0.75rem' }}>
                            {isLoading ? <Spinner animation="border" size="sm" /> : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Login;
